import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './AwardDetails.module.scss';
import ApprovalSection from './ApprovalSection/ApprovalSection';
import HATVNotes from '../HATVNotes/HATVNotes';
import HATVDocument from '../HATVDocument/HATVDocument';
import AIInsight from '../../common/InsightAI/InsightAI';
import HATVLeaderDetails from '../HATVLeaderDetails/HATVLeaderDetails';
import HATVLegs from '../HATVLegs/HATVLegs';
import {
  useLazyGetHATVAwardDetailsQuery,
  useLazyGetHATVTrackingAwardListQuery,
  useLazyGetHATVAwardSummaryQuery,
  useLazyGetHATVAwardDetailsAIInsightSummaryQuery,
  useUpdateHATVAwardDetailsSummaryMutation,
  useLazyGetHATVAwardTODOQuery,
  useUpdateHATVAwardDetailsTODOMutation,
} from '../../../redux/services/hatvAwardDetailsAPI';
import { downloadDocument, useProfileParams } from '../../../utils/common';
import { isEmpty } from 'lodash';
import ErrorHandlingComponent from '../../../components/ErrorHandling/ErrorHandling';
import HATVBCRInfo from '../HATVBCRInfo/HATVBCRInfo';
import SuccessionDocLoader from '../../common/Loaders/SuccessionDocLoader/SuccessionDocLoader';
import { useTranslation } from 'react-i18next';
import ABOBackgroundLoader from '../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader';
import { useAppSelector } from '../../../redux/hooks';
import Icon from '../../common/Icon/Icon';
import { useGetOptionsListQuery } from '../../../redux/services/optionMasterAPI';
import CalendarDropdown from '../HATVDashboard/CalendarDropdown/CalendarDropdown';
import { useMonthOptions } from '../../Context/MonthYearOptionsContext';
import ToastMessage from '../../common/ToastMessage/ToastMessage';
import { toastMessageType } from '../../../types/types';
import { getHatvNotesQueryHook } from '../../../utils/common';
import { getYearFromQMonth } from '../../../utils/common';
import { useLocation } from 'react-router-dom';

interface AwardDetailsType {
  onMonthYearChange: (month: string) => void;
  bcrInfo: BCRInfo;
}
const AwardDetails: React.FC<AwardDetailsType> = ({ onMonthYearChange, bcrInfo }) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const awardApproveNotesAPI = getHatvNotesQueryHook('awardApproval');
  const [awardCodeBasedAwardDetailsData, setAwardCodeBasedAwardDetailsDataList] = useState<any>([]);
  const [trackingAwardListData, setTrackingAwardListData] = useState<any>([]);
  const [selectedTabId, setSelectedTabId] = useState<string>('');

  const [selectedTabData, setSelectedTabData] = useState<any>(null);
  const shouldShowInsightSummaryInAwardSegment = false;
  const [summaryData, setSummaryData] = useState<any>([]);
  const [todoData, setToDoData] = useState<any>([]);
  const [aiInsightsummaryData, setAIInsightSummaryData] = useState<any>([]);
  const [selectedTabAIInsightSummaryData, setSelectedTabAIInsightSummaryData] = useState<any>(null);
  const [selectedTabSummaryData, setSelectedTabSummaryData] = useState<any>(null);
  const [selectedTabToDoData, setSelectedTabToDoData] = useState<any>(null);
  const [isAPIError, setIsAPIError] = useState(false);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [isAPIDataLoading, setAPIDataLoading] = useState(true);
  const currentTabId = selectedTabId;
  const { selectedYearMonth, selectedPYYearMonth } = useMonthOptions();

  const {
    data: periodData,
    isLoading: optionLoading,
    isError: optionError,
  } = useGetOptionsListQuery({
    applicationCode: 'amw',
    optionMasterName: 'awardapproval-yearfilter',
  });
  const optionsData: OptionItem[] = periodData?.body?.options ?? [];

  const yearList = useMemo(() => {
    return optionsData.map(({ code }) => code);
  }, [optionsData]);

  const [selectedYear, setSelectedYear] = useState<string>('');

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const awardCode = searchParams.get('awardcode');
  const qualifiedMonth = searchParams.get('qualifiedmonth');

  useEffect(() => {
    qualifiedMonth && setSelectedYear(getYearFromQMonth(qualifiedMonth));
  }, []);

  useEffect(() => {
    awardCode && setSelectedTabDetailsAndId(awardCode + '#' + qualifiedMonth);
  }, [awardCode, qualifiedMonth]);

  useEffect(() => {
    if (!selectedYear && !qualifiedMonth && yearList.length > 0) {
      setSelectedYear(yearList[0]);
    } else {
      const year = getYearFromQMonth(selectedYearMonth || ''); // 2025
      if (selectedYear !== year) {
        setSelectedYear(year); //2024
      }
    }
  }, [selectedYearMonth, yearList]);
  const isInitialLoading = optionLoading || !selectedYear;

  const performanceYear = selectedYear ? selectedYear?.substring(0, 4) : null;
  const [isStatusUpdate, setIsStatusUpdate] = useState<string>('');

  const [toastConfig, setToastConfig] = useState({
    message: '',
    type: '' as toastMessageType,
    show: false,
  });

  const setShowToast = (show: boolean) => setToastConfig((prev) => ({ ...prev, show }));

  const [
    getTrackingAwardListResponseData,
    { isLoading: isLoadingGetTrackingListAPI, isError, isSuccess: isTrackingListSuccess },
  ] = useLazyGetHATVTrackingAwardListQuery();

  const [
    getHATVAwardSummaryData,
    { isLoading: isLoadingSummaryListAPI, isError: isSummaryAPIError },
  ] = useLazyGetHATVAwardSummaryQuery();

  const [
    getHATVAIInsightSummaryData,
    { isFetching: isLoadingAIInsightSummaryAPIsLoading, isError: isAIInsightSummaryAPIError },
  ] = useLazyGetHATVAwardDetailsAIInsightSummaryQuery();

  const [updateSummaryNote, { isLoading: isLoadingUpdateSummaryAPI }] =
    useUpdateHATVAwardDetailsSummaryMutation();

  const [getHATVAwardTODOData] = useLazyGetHATVAwardTODOQuery();

  const [updateTODONote, { isLoading: isLoadingUpdateTODOAPI }] =
    useUpdateHATVAwardDetailsTODOMutation();

  const [getAwardDetails] = useLazyGetHATVAwardDetailsQuery();

  useEffect(() => {
    if (selectedYear) {
      onMonthYearChange(selectedYear);
    }
  }, [selectedYear]);

  const handleAPIStatus = (status: string) => {
    if (status === 'success') {
      setToastConfig({
        show: true,
        message: `Successfully updated award status`,
        type: 'success',
      });
    } else if (status === 'fail') {
      setToastConfig({
        show: true,
        message: `Failed to update award status. Please try again!`,
        type: 'error',
      });
    }
    setIsStatusUpdate(status);
  };

  useEffect(() => {
    if (!performanceYear || isInitialLoading) {
      return;
    }

    setAPIDataLoading(true);
    setIsAPIError(false);
    setAwardCodeBasedAwardDetailsDataList([]);
    setTrackingAwardListData([]);
    setSummaryData([]);
    setToDoData([]);
    setAIInsightSummaryData([]);
    setSelectedTabData(null);
    setSelectedTabSummaryData(null);
    setSelectedTabToDoData(null);
    setSelectedTabAIInsightSummaryData(null);
    getTrackingAwardListResponseData({
      performanceYear,
      affiliateCode,
      aboNumber,
    })
      .then((resp: any) => {
        const trackingAwards = resp?.data?.body?.trackingAwards || [];
        setTrackingAwardListData(trackingAwards);
        if (isEmpty(trackingAwards)) {
          setAPIDataLoading(false);
          return;
        }
        const detailPromises = trackingAwards.map((awardItem: any) =>
          getAwardDetails({
            performanceYear,
            affiliateCode,
            aboNumber,
            awardCode: awardItem?.trackingAward?.awardCode,
            awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
          })
            .then((awardResp: any) => ({
              trackingAwardCode: awardItem?.trackingAward?.awardCode,
              awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
              ...awardResp.data.body,
              apiStatus: awardResp.data.statusCode,
              isAPIError: false,
              errorMessage: '',
            }))
            .catch((error: any) => {
              console.error(
                'Error fetching award details for:',
                awardItem.trackingAward.awardCode,
                error
              );
              return {
                trackingAwardCode: awardItem?.trackingAward?.awardCode,
                awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
                awardDetail: null,
                apiStatus: error?.errorCode || 500,
                isAPIError: true,
                errorMessage: error?.errorMessage || 'Failed to fetch award details.',
              };
            })
        );

        const summaryPromises = trackingAwards.map((awardItem: any) =>
          getHATVAwardSummaryData({
            performanceYear,
            affiliateCode,
            aboNumber,
            awardCode: awardItem?.trackingAward?.awardCode,
            awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
          })
            .then((summaryResp: any) => ({
              trackingAwardCode: awardItem?.trackingAward?.awardCode,
              awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
              note: summaryResp?.data?.body?.note,
              apiStatus: summaryResp?.data?.statusCode,
              isAPIError: false,
              errorMessage: '',
            }))
            .catch((error: any) => {
              console.error(
                'Error fetching summary for:',
                awardItem.trackingAward.awardCode,
                error
              );
              return {
                trackingAwardCode: awardItem?.trackingAward?.awardCode,
                awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
                note: '',
                apiStatus: error?.errorCode || 500,
                isAPIError: true,
                errorMessage: error?.errorMessage || 'Failed to fetch summary.',
              };
            })
        );

        const todoPromises = trackingAwards.map((awardItem: any) =>
          getHATVAwardTODOData({
            performanceYear,
            affiliateCode,
            aboNumber,
            awardCode: awardItem?.trackingAward?.awardCode,
            awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
          })
            .then((todoResp: any) => ({
              trackingAwardCode: awardItem?.trackingAward?.awardCode,
              awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
              note: todoResp?.data?.body?.note,
              apiStatus: todoResp?.data?.statusCode,
              isAPIError: false,
              errorMessage: '',
            }))
            .catch((error: any) => {
              console.error('Error fetching TODO for:', awardItem.trackingAward.awardCode, error);
              return {
                trackingAwardCode: awardItem?.trackingAward?.awardCode,
                awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
                note: '',
                apiStatus: error?.errorCode || 500,
                isAPIError: true,
                errorMessage: error?.errorMessage || 'Failed to fetch TODO.',
              };
            })
        );

        const aiInsightPromises =
          shouldShowInsightSummaryInAwardSegment &&
          trackingAwards.map((awardItem: any) =>
            getHATVAIInsightSummaryData({
              performanceYear,
              affiliateCode,
              aboNumber,
            })
              .then((aiSummaryResp: any) => ({
                trackingAwardCode: awardItem?.trackingAward?.awardCode,
                awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
                note: aiSummaryResp?.data?.body?.summary,
                apiStatus: aiSummaryResp?.data?.statusCode,
                isAPIError: false,
                errorMessage: '',
              }))
              .catch((error: any) => {
                console.error(
                  'Error fetching AI Insight for:',
                  awardItem.trackingAward.awardCode,
                  error
                );
                return {
                  trackingAwardCode: awardItem?.trackingAward?.awardCode,
                  awardQualifiedMonth: awardItem?.trackingAward?.awardQualifiedMonth,
                  note: '',
                  apiStatus: error?.errorCode || 500,
                  isAPIError: true,
                  errorMessage: error?.errorMessage || 'Failed to fetch AI insight.',
                };
              })
          );

        Promise.all([
          Promise.all(detailPromises),
          Promise.all(summaryPromises),
          Promise.all(todoPromises),
          // Promise.all(aiInsightPromises),
        ])
          .then(([details, summaries, todos]) => {
            // Add [aiInsights] once enbale AI summary UI
            setAwardCodeBasedAwardDetailsDataList(details);
            setSummaryData(summaries);
            setToDoData(todos);
            // setAIInsightSummaryData(aiInsights);
            setAPIDataLoading(false);
          })
          .catch((allPromisesError) => {
            console.error(
              'One or more detail/summary/todo/aiInsight fetches failed:',
              allPromisesError
            );
            setIsAPIError(true);
            setAPIDataLoading(false);
          });
      })
      .catch((trackingListError: any) => {
        console.error('Error fetching tracking award list:', trackingListError);
        setAPIDataLoading(false);
        setIsAPIError(true);
        setTrackingAwardListData([]);
      });
    setIsStatusUpdate('');
  }, [performanceYear, affiliateCode, aboNumber, isInitialLoading, isStatusUpdate]);

  useEffect(() => {
    if (!isEmpty(trackingAwardListData)) {
      const fallbackTabId =
        trackingAwardListData[0]?.trackingAward?.awardCode +
        '#' +
        trackingAwardListData[0]?.trackingAward?.awardQualifiedMonth;

      setSelectedTabDetailsAndId(currentTabId || fallbackTabId);
    }
  }, [
    awardCodeBasedAwardDetailsData,
    trackingAwardListData,
    summaryData,
    todoData,
    // aiInsightsummaryData,
  ]);

  const handleAPIError = () => {
    setIsAPIError(true);
  };

  useEffect(() => {
    if (isError) handleAPIError();
  }, [isError]);

  const setSelectedTabDetailsAndId = (awardCode: string) => {
    setSelectedTabId(awardCode);

    setSelectedTabData(
      awardCodeBasedAwardDetailsData.find(
        (detailsData: any) =>
          detailsData?.trackingAwardCode + '#' + detailsData?.awardQualifiedMonth === awardCode
      )
    );

    setSelectedTabSummaryData(
      summaryData.find(
        (summaryData: any) =>
          summaryData?.trackingAwardCode + '#' + summaryData?.awardQualifiedMonth === awardCode
      )
    );
    setSelectedTabToDoData(
      todoData.find(
        (todoData: any) =>
          todoData?.trackingAwardCode + '#' + todoData?.awardQualifiedMonth === awardCode
      )
    );
    setSelectedTabAIInsightSummaryData(
      aiInsightsummaryData.find(
        (aiSummaryData: any) =>
          aiSummaryData?.trackingAwardCode + '#' + aiSummaryData?.awardQualifiedMonth === awardCode
      )
    );
  };

  const handleSummaryEdit = (notes: string) => {
    const editSummaryPayload = {
      performanceYear: performanceYear,
      awardCode: selectedTabId && selectedTabId?.split('#')[0],
      note: notes,
      awardQualifiedMonth: selectedTabId && selectedTabId?.split('#')[1],
    };
    const summaryDataTemp = JSON.parse(JSON.stringify(summaryData));
    setSummaryData([]);

    updateSummaryNote({
      affiliateCode: affiliateCode,
      aboNumber: aboNumber,
      payload: editSummaryPayload,
    })
      .then((updateSummaryResponse: any) => {
        if (updateSummaryResponse?.data?.statusCode === 200) {
          const updatedNote = updateSummaryResponse?.data?.body?.note;
          const updatedSummaryData = summaryDataTemp.map((data: any) =>
            data?.trackingAwardCode + '#' + data?.awardQualifiedMonth === selectedTabId
              ? { ...data, note: updatedNote }
              : data
          );
          setSummaryData(updatedSummaryData);
        } else {
          const updatedSummaryData = summaryDataTemp.map((data: any) =>
            data?.trackingAwardCode + '#' + data?.awardQualifiedMonth === selectedTabId
              ? {
                  // apiStatus:
                  errorMessage: updateSummaryResponse?.data?.error?.errorMessage,
                  isAPIError: true,
                  trackingAwardCode: data?.trackingAwardCode,
                  awardQualifiedMonth: data?.awardQualifiedMonth,
                  apiStatus: updateSummaryResponse?.data?.status,
                  notes: undefined,
                }
              : data
          );
          setSummaryData(updatedSummaryData);
        }
      })
      .catch((error) => {
        console.error('ERROR >>>', error);
        const updatedSummaryData = summaryDataTemp.map((data: any) =>
          data?.trackingAwardCode + '#' + data?.awardQualifiedMonth === selectedTabId
            ? {
                errorMessage: error?.data,
                isAPIError: true,
                trackingAwardCode: data?.trackingAwardCode,
                awardQualifiedMonth: data?.awardQualifiedMonth,
                apiStatus: error?.status,
                notes: undefined,
              }
            : data
        );
        setSummaryData(updatedSummaryData);
      });
  };

  const handleTODOEdit = (notes: string) => {
    const editToDoPayload = {
      performanceYear: performanceYear,
      awardCode: selectedTabId && selectedTabId?.split('#')[0],
      note: notes,
      awardQualifiedMonth: selectedTabId && selectedTabId?.split('#')[1],
    };
    const todoDataTemp = JSON.parse(JSON.stringify(todoData));
    setToDoData([]);
    updateTODONote({
      affiliateCode: affiliateCode,
      aboNumber: aboNumber,
      payload: editToDoPayload,
    })
      .then((updateToDoResponse: any) => {
        if (updateToDoResponse?.data?.statusCode === 200) {
          const updatedNote = updateToDoResponse?.data?.body?.note;
          const updatedToDoData = todoDataTemp.map((data: any) =>
            data?.trackingAwardCode + '#' + data?.awardQualifiedMonth === selectedTabId
              ? { ...data, note: updatedNote }
              : data
          );
          setToDoData(updatedToDoData);
        } else {
          const updatedToDoData = todoDataTemp.map((data: any) =>
            data?.trackingAwardCode + '#' + data?.awardQualifiedMonth === selectedTabId
              ? {
                  errorMessage: updateToDoResponse?.data?.error?.errorMessage,
                  isAPIError: true,
                  trackingAwardCode: data?.trackingAwardCode,
                  awardQualifiedMonth: data?.awardQualifiedMonth,
                  apiStatus: updateToDoResponse?.data?.status,
                  notes: undefined,
                }
              : data
          );
          setToDoData(updatedToDoData);
        }
      })
      .catch((error) => {
        console.error('ERROR >>>', error);
        const updatedToDoData = todoDataTemp.map((data: any) =>
          data.trackingAwardCode + '#' + data?.awardQualifiedMonth === selectedTabId
            ? {
                errorMessage: error?.data,
                isAPIError: true,
                trackingAwardCode: data?.trackingAwardCode,
                awardQualifiedMonth: data?.awardQualifiedMonth,
                apiStatus: error?.status,
                notes: undefined,
              }
            : data
        );
        setToDoData(updatedToDoData);
      });
  };

  const handleSelectedCode = useCallback((value: string) => {
    setSelectedYear(value);
    selectedPYYearMonth(value);
  }, []);
  useEffect(() => {
    if (!isEmpty(trackingAwardListData))
      setSelectedTabDetailsAndId(
        selectedTabId
          ? selectedTabId
          : trackingAwardListData[0]?.trackingAward?.awardCode +
              '#' +
              trackingAwardListData[0]?.trackingAward?.awardQualifiedMonth
      );
  }, [
    awardCodeBasedAwardDetailsData,
    trackingAwardListData,
    summaryData,
    todoData,
    aiInsightsummaryData,
  ]);

  const getAssignedNextApproverName = (approvername: string) => {
    let assignedApproverId = '';
    let nextApproverId = '';
    if (
      selectedTabData?.awardDetail?.awardApproval?.finalApprovalStatus !== 3 &&
      selectedTabData?.awardDetail?.awardApproval?.finalApprovalStatus !== 9
    ) {
      selectedTabData?.awardDetail?.awardApproval?.awardApprovers?.map((approver: any) => {
        if (approver?.approvalStatus !== 3 && approver?.approvalStatus !== 9) {
          if (assignedApproverId === '') {
            assignedApproverId = approver?.userNativeId || '';
          } else if (assignedApproverId !== '' && nextApproverId === '') {
            nextApproverId = approver?.userNativeId || '';
          }
        }
      });
    }
    return approvername === 'assign'
      ? assignedApproverId
      : approvername === 'next'
        ? nextApproverId
        : '';
  };

  return (
    <div className={`award_details_container ${styles.award_details_container}`}>
      <div className={styles.year_playbook_wrapper}>
        {isInitialLoading ? (
          <>
            <div className={`details_loader animate ${styles.calender_loader}`} />
          </>
        ) : (
          <div className={styles.time_selector}>
            <CalendarDropdown
              monthYearList={yearList}
              onselectionMonthYear={handleSelectedCode}
              selectedMonth={selectedYear}
              isOnlyYear={true}
              isLoading={isInitialLoading}
              isError={optionError}
            />
          </div>
        )}

        <div className={`separator_line ${styles.separator_line}`} />

        <div
          onClick={() => downloadDocument('playbook-hatv')}
          className={`playbook_link ${styles.playbook_link}`}
        >
          <Icon
            name={'playbook_icon'}
            size={14}
            className={`playbook_icon ${styles.playbook_icon}`}
            color={currentTheme === 'light' ? '#2C2C2C' : '#D8D8D9'}
          />
          <span className={`playbook_text ${styles.playbook_text}`}>{t('viewPlaybook')}</span>
        </div>
      </div>
      <div className={styles.section_wrapper}>
        {isAPIDataLoading ? (
          <ABOBackgroundLoader num={1} heading={''} />
        ) : isAPIError ? (
          <ErrorHandlingComponent error={'genericError'} />
        ) : selectedTabData && !isAPIDataLoading ? (
          <ApprovalSection
            key={selectedTabId || performanceYear}
            tabs={(trackingAwardListData || []).map((trackingAward: any) => ({
              awardCode: trackingAward?.trackingAward?.awardCode,
              awardName: trackingAward?.trackingAward?.awardName,
              awardTagName: trackingAward?.trackingAward?.awardTagName,
              awardTagCode: trackingAward?.trackingAward?.awardTagCode,
              awardQualifiedMonth: trackingAward?.trackingAward?.awardQualifiedMonth,
              performanceYear: performanceYear,
            }))}
            selectedTabId={selectedTabId}
            onTabChange={setSelectedTabDetailsAndId}
            approvalStatus={selectedTabData?.awardDetail?.awardApproval?.finalApprovalStatus}
            assignedApprover={getAssignedNextApproverName('assign')}
            nextApprover={getAssignedNextApproverName('next')}
            caseEvaluator={selectedTabData?.awardDetail?.hatvAdmin?.userNativeId}
            hatvRoutingRoleIndicators={selectedTabData?.awardDetail?.hatvRoutingRoleIndicators}
            hatvApprovalRoleIndicators={selectedTabData?.awardDetail?.hatvApprovalRoleIndicators}
            HATVSummaryData={selectedTabSummaryData}
            handleSummaryEdit={handleSummaryEdit}
            handleTODOEdit={handleTODOEdit}
            HATVToDoData={selectedTabToDoData}
            isToDoLoading={isLoadingUpdateTODOAPI}
            isSummaryLoading={isLoadingUpdateSummaryAPI}
            selectedTabDetailsData={selectedTabData}
            setIsStatusUpdate={handleAPIStatus}
          />
        ) : (
          t('noDataAvailable')
        )}
      </div>

      {/* TODO: hardcoded data added for now replace it with API response */}
      {shouldShowInsightSummaryInAwardSegment &&
        (isLoadingAIInsightSummaryAPIsLoading || isAPIDataLoading ? (
          <div className={`${styles.ai_insight_wrapper} animate`}></div>
        ) : (
          <div className={styles.ai_summary}>
            <AIInsight
              content={selectedTabAIInsightSummaryData?.note}
              showDropdown={true}
              isDropdownEnabled={true}
              flowerIconSize={24}
              infoIconSize={12}
              title={'ai_summary'}
              titleSize={16}
            />
          </div>
        ))}
      <div className={styles.section_wrapper}>
        <HATVNotes
          type={'awardapproval'}
          selectedTabData={selectedTabData}
          isStatusUpdate={isStatusUpdate}
          hatvNotesAPI={awardApproveNotesAPI}
        />
      </div>
      <div className={styles.section_wrapper}>
        <HATVDocument type={'awardapproval'} selectedTabData={selectedTabData} />
      </div>
      <div className={styles.section_wrapper}>
        <HATVLeaderDetails selectedYearMonth={selectedYearMonth ?? ''} />
      </div>
      <div className={styles.section_wrapper} data-testid="award_Legs">
        <HATVLegs selectedYearMonth={selectedYear ?? ''} />
      </div>
      <div className={styles.section_wrapper}>
        {bcrInfo?.isBCRFetching || bcrInfo?.isBCRLoading ? (
          <SuccessionDocLoader num={1} heading={t('BCR_heading')} />
        ) : (
          <>
            <div className={styles.title} data-testid="BCR_Info_testId">
              {t('BCR_heading')}
            </div>
            <HATVBCRInfo
              tableData={bcrInfo?.isBCRAPIError ? [] : bcrInfo?.bcrData?.body}
              isLoading={false}
            />
          </>
        )}
      </div>
      <ToastMessage
        message={toastConfig.message}
        toastType={toastConfig.type}
        showToast={toastConfig.show}
        setShowToast={setShowToast}
        customStyles={{
          top: '64px',
        }}
        autoDismissal
      />
    </div>
  );
};

export default AwardDetails;
